import { api } from './api';

export interface Product {
  readonly productId: string;
  readonly name: string;
  readonly description: string;
  readonly category: string;
  readonly basePrice: number;
  readonly imageKey: string | null;
  readonly previewImageKeys: readonly string[];
  readonly fileKey: string | null;
  readonly originalFileName: string | null;
  readonly materials: readonly string[];
  readonly dimensions: {
    readonly width: number;
    readonly height: number;
    readonly depth: number;
  } | null;
  readonly isActive: boolean;
  readonly sortOrder: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateProductInput {
  readonly name: string;
  readonly description: string;
  readonly category: string;
  readonly basePrice: number;
  readonly materials: readonly string[];
  readonly dimensions?: {
    readonly width: number;
    readonly height: number;
    readonly depth: number;
  } | null;
  readonly sortOrder?: number;
}

export interface ProductListResponse {
  readonly data: readonly Product[];
  readonly pagination: { readonly lastKey: string | null };
}

export const productsApi = {
  list: async (params?: { category?: string; lastKey?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.category) query.set('category', params.category);
    if (params?.lastKey) query.set('lastKey', params.lastKey);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    const result = await api.get<readonly Product[]>(`/products${qs ? `?${qs}` : ''}`);
    return result as unknown as ProductListResponse;
  },

  get: async (id: string) => {
    const result = await api.get<Product>(`/products/${id}`);
    return result.data!;
  },

  create: async (input: CreateProductInput) => {
    const result = await api.post<Product>('/products', input);
    return result.data!;
  },

  update: async (id: string, input: Partial<CreateProductInput> & { previewImageKeys?: readonly string[] }) => {
    const result = await api.put<Product>(`/products/${id}`, input);
    return result.data!;
  },

  delete: async (id: string) => {
    await api.delete(`/products/${id}`);
  },

  getImageUploadUrl: async (id: string, index: number, contentType: string) => {
    const result = await api.post<{ uploadUrl: string; imageKey: string; index: number }>(
      `/products/${id}/image-url`,
      { index, contentType },
    );
    return result.data!;
  },

  getFileDownloadUrl: async (id: string): Promise<string> => {
    const result = await api.get<{ downloadUrl: string }>(`/products/${id}/file-download-url`);
    return result.data!.downloadUrl;
  },

  getFileUploadUrl: async (id: string, fileName: string, fileSize: number, contentType: string) => {
    const result = await api.post<{ uploadUrl: string; fileKey: string }>(
      `/products/${id}/file-url`,
      { fileName, fileSize, contentType },
    );
    return result.data!;
  },
};
